import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  GraduationCap, BookOpen, Award, Activity, Brain, Building2,
  BarChart3, FileText, Trophy, Clock, ChevronRight, ShieldCheck,
  AlertTriangle, CheckCircle2, Sparkles, Lock, Loader2, Download,
  Wallet, ScrollText, Printer, Eye, QrCode,
} from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';
import AiCoachPanel from './training/AiCoachPanel';
import NexusUniversityPanel from './training/NexusUniversityPanel';
import SimulatorsPanel from './training/SimulatorsPanel';
const TOKEN_KEY = 'nexus_token';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const StatTile = ({ icon: Icon, label, value, sub, testid, accent = 'amber' }) => {
  const palette = {
    amber: 'border-amber-400/30 bg-amber-500/5 text-amber-300',
    emerald: 'border-emerald-400/30 bg-emerald-500/5 text-emerald-300',
    blue: 'border-blue-400/30 bg-blue-500/5 text-blue-300',
    slate: 'border-slate-500/30 bg-slate-500/5 text-slate-200',
  }[accent] || 'border-slate-500/30 bg-slate-500/5 text-slate-200';
  return (
    <div className={`rounded-lg border-2 ${palette} p-3 flex items-start gap-3`} data-testid={testid}>
      <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-300 truncate">{label}</p>
        <p className="text-xl font-bold text-white mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-1 truncate">{sub}</p>}
      </div>
    </div>
  );
};

const CourseCard = ({ course, progress, assignment, onOpen }) => {
  const pct = progress?.completed_pct || 0;
  const status = assignment?.status || progress?.status || 'NOT_STARTED';
  return (
    <Card className="bg-[#131A2A] border-[#1E293B] hover:border-amber-500/50 transition-colors cursor-pointer"
          data-testid={`course-card-${course.id}`}
          onClick={() => onOpen(course)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400">{course.category.replace(/_/g, ' ')}</p>
            <h4 className="text-base font-bold text-white mt-1">{course.code} — {course.title}</h4>
            <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{course.summary}</p>
          </div>
          <Badge className="bg-slate-800 border border-slate-600 text-slate-300 text-[10px] font-mono uppercase flex-shrink-0">
            {course.estimated_hours}h
          </Badge>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
          <span className="text-slate-500">{course.lessons?.length || 0} lessons</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-500">{course.final_exam_size || 50} q final · {course.passing_score}%</span>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] font-mono mb-1">
            <span className="text-slate-400">{status.replace(/_/g, ' ')}</span>
            <span className="text-amber-300 font-bold" data-testid={`course-pct-${course.id}`}>{pct}%</span>
          </div>
          <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end">
          <span className="text-amber-300 text-xs font-semibold inline-flex items-center gap-1">
            Open course <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// =====================================================================
// Certification Wallet — earned certs grid with View / Download / Print
// =====================================================================
const downloadCertPdf = async (cert) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/training-center/certificates/${cert.id}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `nexus-certificate-${cert.id}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (e) { toast.error(e.message); }
};

const printCertPdf = async (cert) => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/training-center/certificates/${cert.id}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) { w.addEventListener('load', () => { try { w.print(); } catch (e) { /* noop */ } }); }
  } catch (e) { toast.error(e.message); }
};

const CertificationWallet = ({ earned, navigate }) => {
  if (!earned || earned.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-slate-700 rounded-lg" data-testid="wallet-empty">
        <Wallet className="w-12 h-12 mx-auto text-slate-600 mb-3" />
        <p className="text-base text-white font-semibold">Your wallet is empty</p>
        <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
          Earned certificates will appear here after your training administrator issues them.
          Each card lets you view, download as PDF, or print your credential on demand.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-white">Certification Wallet</h3>
          <p className="text-xs text-slate-400">Your earned credentials · {earned.length} active</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wallet-cert-grid">
        {earned.map(cert => (
          <Card key={cert.id} className="bg-gradient-to-br from-[#0F1B33] via-[#131A2A] to-[#0A0F1A] border-2 border-amber-500/40 overflow-hidden"
                data-testid={`wallet-card-${cert.id}`}>
            <CardContent className="p-0">
              {/* Top stripe */}
              <div className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 px-5 py-3 flex items-center justify-between border-b border-amber-500/30">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-amber-300" />
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300 font-bold">Verified Credential</p>
                </div>
                <Badge className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[10px] font-mono">
                  {cert.status || 'ISSUED'}
                </Badge>
              </div>

              <div className="p-5">
                <div className="flex items-start gap-4">
                  <img src="/training-center/seal.png" alt="" className="w-16 h-16 rounded-full flex-shrink-0 shadow-lg" />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base font-extrabold text-white leading-tight">{cert.cert_title}</h4>
                    <p className="text-xs text-slate-300 mt-1.5">Issued to</p>
                    <p className="text-sm text-white font-semibold">{cert.user_name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-slate-400">Cert ID</p>
                    <p className="text-slate-200 font-mono text-[11px] truncate">{cert.id}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-slate-400">Issued</p>
                    <p className="text-slate-200">{cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-slate-400">Expires</p>
                    <p className="text-slate-200">{cert.expires_at ? new Date(cert.expires_at).toLocaleDateString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-mono uppercase tracking-wider text-slate-400">Verification</p>
                    <p className="text-amber-300 font-mono text-[11px] truncate inline-flex items-center gap-1" data-testid={`cert-verify-id-${cert.id}`}>
                      <QrCode className="w-3 h-3" /> {formatVerifyId(cert)}
                    </p>
                  </div>
                </div>

                {cert.course_ids && cert.course_ids.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {cert.course_ids.map(cid => (
                      <Badge key={cid} className="bg-slate-800 border border-slate-600 text-slate-300 text-[9px] font-mono">{cid}</Badge>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-700/60">
                  <Button onClick={() => navigate(`/training-center/certificate/${cert.verification_uuid}`)}
                          size="sm" className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
                          data-testid={`wallet-view-${cert.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                  <Button onClick={() => downloadCertPdf(cert)} size="sm" variant="outline"
                          className="border-slate-500 !text-slate-200 hover:bg-slate-800"
                          data-testid={`wallet-download-${cert.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                  <Button onClick={() => printCertPdf(cert)} size="sm" variant="outline"
                          className="border-slate-500 !text-slate-200 hover:bg-slate-800"
                          data-testid={`wallet-print-${cert.id}`}>
                    <Printer className="w-3.5 h-3.5 mr-1" /> Print
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// Student Transcript — completed courses, scores, GPA, hours
// =====================================================================
const StudentTranscript = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiCall('/api/training-center/transcript');
        if (alive) setData(r);
      } catch (e) {
        if (alive) toast.error(e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/training-center/transcript/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `nexus-transcript.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return <p className="text-slate-400 italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading transcript…</p>;
  }
  if (!data) {
    return <p className="text-slate-400">No transcript data available.</p>;
  }

  const s = data.summary || {};
  const courses = data.courses || [];
  const attempts = data.exam_attempts || [];
  const certs = data.certificates || [];

  const statusPill = (status) => {
    const palette = {
      COMPLETED: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200',
      IN_PROGRESS: 'bg-blue-500/20 border-blue-500/40 text-blue-200',
      EXAM_READY: 'bg-amber-500/20 border-amber-500/40 text-amber-200',
      ASSIGNED: 'bg-slate-500/20 border-slate-500/40 text-slate-200',
    }[status] || 'bg-slate-500/20 border-slate-500/40 text-slate-200';
    return <Badge className={`${palette} border text-[10px] font-mono`}>{(status || '—').replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div data-testid="transcript-view">
      {/* Header */}
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">Official Student Transcript</h3>
          <p className="text-xs text-slate-400">{data.user?.name || data.user?.username} · Generated {new Date(s.generated_at).toLocaleString()}</p>
        </div>
        <Button onClick={downloadPdf} disabled={downloading}
                className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
                data-testid="transcript-download-pdf">
          {downloading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating…</> :
            <><Download className="w-4 h-4 mr-1.5" /> Download Transcript PDF</>}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="transcript-summary">
        <div className="rounded-lg border-2 border-amber-400/30 bg-amber-500/5 p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">GPA</p>
          <p className="text-3xl font-extrabold text-white mt-1" data-testid="transcript-gpa">{s.gpa_4_scale ?? '—'}<span className="text-base text-slate-400 font-mono">/4.0</span></p>
        </div>
        <div className="rounded-lg border-2 border-emerald-400/30 bg-emerald-500/5 p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Training Hours</p>
          <p className="text-3xl font-extrabold text-white mt-1" data-testid="transcript-hours">{s.completed_training_hours ?? 0}<span className="text-base text-slate-400 font-mono">/{s.total_training_hours ?? 0}</span></p>
        </div>
        <div className="rounded-lg border-2 border-blue-400/30 bg-blue-500/5 p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-blue-300">Courses Completed</p>
          <p className="text-3xl font-extrabold text-white mt-1">{s.courses_completed ?? 0}<span className="text-base text-slate-400 font-mono">/{s.courses_assigned ?? 0}</span></p>
        </div>
        <div className="rounded-lg border-2 border-slate-500/30 bg-slate-500/5 p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-300">Active Certs</p>
          <p className="text-3xl font-extrabold text-white mt-1">{s.certificates_active ?? 0}<span className="text-base text-slate-400 font-mono">/{s.certificates_total ?? 0}</span></p>
        </div>
      </div>

      {/* Courses */}
      <Card className="bg-[#131A2A] border-[#1E293B] mb-6">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
            <h4 className="text-sm font-bold text-white flex items-center gap-2"><BookOpen className="w-4 h-4 text-amber-300" /> Course Record</h4>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{courses.length} course{courses.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transcript-courses-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-700/60">
                  <th className="px-4 py-2 text-left">Code</th>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-center">Hours</th>
                  <th className="px-4 py-2 text-center">Best Score</th>
                  <th className="px-4 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {courses.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-500 italic">No courses on transcript.</td></tr>
                ) : courses.map(c => (
                  <tr key={c.course_id} className="border-b border-slate-700/30 hover:bg-slate-800/40"
                      data-testid={`transcript-course-${c.course_id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-amber-300">{c.code}</td>
                    <td className="px-4 py-2.5 text-slate-200">{c.title}</td>
                    <td className="px-4 py-2.5 text-center text-slate-300 font-mono">{c.estimated_hours}</td>
                    <td className="px-4 py-2.5 text-center font-mono">
                      {c.best_score_pct != null ? (
                        <span className={c.best_score_pct >= (c.passing_score || 70) ? 'text-emerald-300 font-bold' : 'text-red-300'}>
                          {Math.round(c.best_score_pct)}%
                        </span>
                      ) : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center">{statusPill(c.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Exam attempts */}
      {attempts.length > 0 && (
        <Card className="bg-[#131A2A] border-[#1E293B] mb-6">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-slate-700/60">
              <h4 className="text-sm font-bold text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-300" /> Exam Attempt History</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transcript-attempts-table">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-700/60">
                    <th className="px-4 py-2 text-left">Course</th>
                    <th className="px-4 py-2 text-center">Score</th>
                    <th className="px-4 py-2 text-center">Result</th>
                    <th className="px-4 py-2 text-right">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.slice(0, 50).map((a, idx) => (
                    <tr key={`${a.id || idx}`} className="border-b border-slate-700/30">
                      <td className="px-4 py-2 text-slate-200 text-xs">{a.course_title || a.course_id}</td>
                      <td className="px-4 py-2 text-center font-mono">{a.score_pct != null ? `${Math.round(a.score_pct)}%` : '—'}</td>
                      <td className="px-4 py-2 text-center">
                        {a.passed ? (
                          <Badge className="bg-emerald-500/20 border-emerald-500/40 text-emerald-200 border text-[10px] font-mono">PASS</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 border-red-500/40 text-red-200 border text-[10px] font-mono">FAIL</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-[11px] text-slate-400 font-mono">
                        {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certifications snapshot */}
      {certs.length > 0 && (
        <Card className="bg-[#131A2A] border-[#1E293B]">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-slate-700/60">
              <h4 className="text-sm font-bold text-white flex items-center gap-2"><Award className="w-4 h-4 text-amber-300" /> Certifications</h4>
            </div>
            <ul className="divide-y divide-slate-700/40">
              {certs.map(c => (
                <li key={c.id} className="px-5 py-2.5 flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold truncate">{c.cert_title}</p>
                    <p className="text-[11px] font-mono text-slate-400">
                      {c.id} · Issued {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : '—'}
                      {c.expires_at ? ` · Expires ${new Date(c.expires_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <Badge className={`text-[10px] font-mono ${
                    c.status === 'ISSUED' ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200' :
                    c.status === 'PENDING_APPROVAL' ? 'bg-amber-500/20 border-amber-500/40 text-amber-200' :
                    'bg-slate-500/20 border-slate-500/40 text-slate-200'
                  } border`}>{c.status}</Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const formatVerifyId = (cert) => {
  if (!cert?.verification_uuid) return '(pending)';
  const dt = cert.issued_at ? new Date(cert.issued_at) : new Date();
  const yy = String(dt.getFullYear() % 100).padStart(2, '0');
  const tail = cert.verification_uuid.replace(/-/g, '').slice(-4).toUpperCase();
  return `NexusTraining-${yy}-${tail}`;
};


const TrainingCenter = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [certData, setCertData] = useState({ catalog: [], earned: [], eligibility: {} });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c, a, certs] = await Promise.all([
        apiCall('/api/training-center/stats'),
        apiCall('/api/training-center/courses'),
        apiCall('/api/training-center/assignments'),
        apiCall('/api/training-center/certifications'),
      ]);
      setStats(s);
      setCourses(c.courses || []);
      setAssignments(a.assignments || []);
      setCertData(certs || { catalog: [], earned: [], eligibility: {} });
      const pmap = {};
      await Promise.all((c.courses || []).map(async crs => {
        try {
          const r = await apiCall(`/api/training-center/courses/${crs.id}`);
          pmap[crs.id] = r.progress;
        } catch (e) { /* swallow */ }
      }));
      setProgressMap(pmap);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, c, a, certs] = await Promise.all([
          apiCall('/api/training-center/stats'),
          apiCall('/api/training-center/courses'),
          apiCall('/api/training-center/assignments'),
          apiCall('/api/training-center/certifications'),
        ]);
        if (!alive) return;
        setStats(s);
        setCourses(c.courses || []);
        setAssignments(a.assignments || []);
        setCertData(certs || { catalog: [], earned: [], eligibility: {} });
        const pmap = {};
        await Promise.all((c.courses || []).map(async crs => {
          try {
            const r = await apiCall(`/api/training-center/courses/${crs.id}`);
            pmap[crs.id] = r.progress;
          } catch (e) { /* swallow */ }
        }));
        if (alive) setProgressMap(pmap);
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const assignmentByCourse = useMemo(() => {
    const m = {};
    assignments.forEach(a => { m[a.course_id] = a; });
    return m;
  }, [assignments]);

  const openCourse = (course) => navigate(`/training-center/course/${course.id}`);

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white" data-testid="training-center-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#1E293B]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1B33] via-[#0A0F1A] to-[#0A0F1A]" />
        <div className="absolute right-0 top-0 bottom-0 w-[420px] opacity-20 pointer-events-none hidden md:block">
          <img src="/training-center/seal.png" alt="" className="w-full h-full object-contain object-right" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 flex items-center gap-5 flex-wrap">
          <img src="/training-center/seal.png" alt="Nexus Training Center" className="w-20 h-20 rounded-full shadow-xl flex-shrink-0" data-testid="trc-seal" />
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300">NEXUS INVESTIGATION AGENCY</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white mt-1">Nexus Training Center</h1>
            <p className="text-sm text-slate-300 mt-1 font-mono">Training · Certifications · Simulations · Compliance</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-end mb-3">
          <Button
            onClick={async () => {
              try {
                const res = await fetch(`${BACKEND_URL}/api/training-center/transcript/pdf`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'nexus-transcript.pdf';
                link.click();
                URL.revokeObjectURL(link.href);
              } catch (e) { toast.error(e.message); }
            }}
            variant="outline"
            size="sm"
            className="border-slate-500 !text-slate-200 hover:bg-slate-800"
            data-testid="trc-download-transcript"
          >
            <Download className="w-4 h-4 mr-1.5" /> Download Transcript (PDF)
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="trc-stats-row">
          <StatTile testid="stat-progress" icon={Activity} label="Progress" value={`${stats?.progress_pct ?? 0}%`} sub="Overall" accent="amber" />
          <StatTile testid="stat-completed" icon={CheckCircle2} label="Completed" value={stats?.courses_completed ?? 0} sub={`of ${stats?.courses_assigned ?? 0}`} accent="emerald" />
          <StatTile testid="stat-certs" icon={Award} label="Certifications" value={stats?.certifications_earned ?? 0} sub="Active" accent="amber" />
          <StatTile testid="stat-pending" icon={AlertTriangle} label="Pending" value={stats?.pending_assignments ?? 0} sub="Assignments" accent="blue" />
          <StatTile testid="stat-exam-avg" icon={BarChart3} label="Exam Avg" value={`${stats?.exam_average ?? 0}%`} sub={`${stats?.exam_attempts_count ?? 0} attempts`} accent="slate" />
          <StatTile testid="stat-rank" icon={Trophy} label="Rank" value={stats?.current_rank ?? 'Trainee'} sub="Current" accent="amber" />
          <StatTile testid="stat-ce-hours" icon={Clock} label="CE Hours" value={stats?.ce_hours ?? 0} sub="Earned" accent="emerald" />
        </div>

        <div className="mt-7">
          <Tabs defaultValue="courses">
            <TabsList className="bg-[#131A2A] border border-[#1E293B]">
              <TabsTrigger value="courses" data-testid="trc-tab-courses"><BookOpen className="w-4 h-4 mr-1.5" /> Nexus Academy</TabsTrigger>
              <TabsTrigger value="certifications" data-testid="trc-tab-certifications"><Award className="w-4 h-4 mr-1.5" /> Certifications</TabsTrigger>
              <TabsTrigger value="wallet" data-testid="trc-tab-wallet"><Wallet className="w-4 h-4 mr-1.5" /> Wallet</TabsTrigger>
              <TabsTrigger value="transcript" data-testid="trc-tab-transcript"><ScrollText className="w-4 h-4 mr-1.5" /> Transcript</TabsTrigger>
              <TabsTrigger value="simulations" data-testid="trc-tab-simulations"><Brain className="w-4 h-4 mr-1.5" /> Simulators</TabsTrigger>
              <TabsTrigger value="university" data-testid="trc-tab-university"><Building2 className="w-4 h-4 mr-1.5" /> Nexus University</TabsTrigger>
              <TabsTrigger value="coach" data-testid="trc-tab-coach"><Sparkles className="w-4 h-4 mr-1.5" /> AI Coach</TabsTrigger>
            </TabsList>

            <TabsContent value="courses" className="pt-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-white">Course Catalog</h3>
                <p className="text-xs text-slate-400 font-mono">{courses.length} course{courses.length === 1 ? '' : 's'} available</p>
              </div>
              {loading ? <p className="text-slate-400 italic">Loading…</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trc-course-grid">
                  {courses.map(c => (
                    <CourseCard key={c.id} course={c} progress={progressMap[c.id]}
                                assignment={assignmentByCourse[c.id]} onOpen={openCourse} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="certifications" className="pt-5 space-y-3">
              <h3 className="text-lg font-semibold text-white mb-2">Certification Center</h3>
              <p className="text-xs text-slate-400 mb-4 max-w-2xl">
                Complete the required courses and pass each final exam at the required score. Eligible certifications can be issued instantly, signed, and verifiable via the public verification portal.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trc-cert-grid">
                {certData.catalog.map(cert => {
                  const earned = certData.earned.find(e => e.cert_id === cert.id);
                  const elig = certData.eligibility[cert.id];
                  const reqProgress = elig?.course_scores || [];
                  const completedCount = reqProgress.filter(s => s.status === 'COMPLETED').length;
                  const totalReq = cert.course_ids.length;
                  return (
                    <Card key={cert.id} className={`${earned ? 'border-emerald-500/50' : elig?.eligible ? 'border-amber-500/50' : 'border-slate-700 opacity-90'} bg-[#131A2A]`}
                          data-testid={`cert-card-${cert.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0">
                            <img src="/training-center/seal.png" alt="" className={`w-12 h-12 rounded-full ${earned ? 'opacity-100' : elig?.eligible ? 'opacity-90' : 'opacity-50 grayscale'}`} />
                            {!earned && !elig?.eligible && (
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-800 border-2 border-[#131A2A] flex items-center justify-center" title="Locked — complete required courses first">
                                <Lock className="w-2.5 h-2.5 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">{cert.passing_score}% PASS · {cert.validity_months} MO</p>
                            <h4 className="text-base font-bold text-white mt-1">{cert.title}</h4>
                            <p className="text-xs text-slate-400 mt-1">{cert.summary}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {cert.course_ids.map(cid => (
                            <Badge key={cid} className="bg-slate-800 border border-slate-600 text-[10px] font-mono text-slate-300">{cid}</Badge>
                          ))}
                        </div>

                        {/* Progress toward this cert */}
                        {!earned && !elig?.eligible && totalReq > 0 && (
                          <div className="mt-3" data-testid={`cert-progress-${cert.id}`}>
                            <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                              <span className="text-slate-400">Required courses</span>
                              <span className="text-slate-300 font-bold">{completedCount}/{totalReq}</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500" style={{ width: `${Math.round(100 * completedCount / totalReq)}%` }} />
                            </div>
                            <ul className="mt-2 space-y-1 text-[11px]">
                              {reqProgress.map(s => (
                                <li key={s.course_id} className="flex items-center gap-1.5 text-slate-300">
                                  {s.status === 'COMPLETED' ? (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                  ) : (
                                    <span className="w-3 h-3 rounded-full border border-slate-500 flex-shrink-0 inline-flex" />
                                  )}
                                  <span className="font-mono">{s.course_id}</span>
                                  <span className="text-slate-500 ml-auto">
                                    {s.status === 'COMPLETED' ? `${s.score}%` : s.status === 'NOT_STARTED' ? 'not started' : 'in progress'}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-t border-slate-700/60">
                          {earned ? (
                            <div className="grid grid-cols-2 gap-2">
                              <Button onClick={() => navigate(`/training-center/certificate/${earned.verification_uuid}`)} size="sm" className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid={`cert-view-${cert.id}`}>
                                <ShieldCheck className="w-4 h-4 mr-1.5" /> View
                              </Button>
                              <Button
                                onClick={async () => {
                                  try {
                                    const url = `${BACKEND_URL}/api/training-center/certificates/${earned.id}/pdf`;
                                    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('nexus_token')}` } });
                                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                    const blob = await res.blob();
                                    const link = document.createElement('a');
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `nexus-certificate-${earned.id}.pdf`;
                                    link.click();
                                    URL.revokeObjectURL(link.href);
                                  } catch (e) { toast.error(e.message); }
                                }}
                                size="sm"
                                variant="outline"
                                className="border-slate-500 !text-slate-200 hover:bg-slate-800"
                                data-testid={`cert-pdf-${cert.id}`}
                              >
                                <Download className="w-4 h-4 mr-1.5" /> PDF
                              </Button>
                            </div>
                          ) : elig?.eligible ? (
                            <Button onClick={async () => {
                              try {
                                const r = await apiCall(`/api/training-center/certifications/${cert.id}/issue`, { method: 'POST' });
                                toast.success(`Certificate issued: ${r.certificate.id}`);
                                refresh();
                                navigate(`/training-center/certificate/${r.certificate.verification_uuid}`);
                              } catch (e) { toast.error(e.message); }
                            }} size="sm" className="w-full bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid={`cert-issue-${cert.id}`}>
                              <Award className="w-4 h-4 mr-1.5" /> Issue certificate
                            </Button>
                          ) : (
                            <Button disabled size="sm" className="w-full bg-slate-800 !text-slate-500 cursor-not-allowed border border-slate-700" data-testid={`cert-locked-${cert.id}`}>
                              <Lock className="w-4 h-4 mr-1.5" /> Locked — pass required courses
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="wallet" className="pt-5" data-testid="trc-tab-content-wallet">
              <CertificationWallet earned={certData.earned} navigate={navigate} />
            </TabsContent>

            <TabsContent value="transcript" className="pt-5" data-testid="trc-tab-content-transcript">
              <StudentTranscript />
            </TabsContent>

            <TabsContent value="simulations" className="pt-5" data-testid="trc-tab-content-simulations">
              <SimulatorsPanel />
            </TabsContent>

            <TabsContent value="university" className="pt-5" data-testid="trc-tab-content-university">
              <NexusUniversityPanel />
            </TabsContent>

            <TabsContent value="coach" className="pt-5" data-testid="trc-tab-content-coach">
              <AiCoachPanel />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default TrainingCenter;
